import { toast } from '@heroui/react'
import { create } from 'zustand'

import type { GetVehiclesParams } from '@/api/vehicles.api'
import { vehiclesApi } from '@/api/vehicles.api'
import i18n from '@/i18n'
import type { CreateVehicleRequest, UpdateVehicleRequest, Vehicle as ApiVehicle } from '@/types/vehicle'
import type { Vehicle as UiVehicle } from '@/types/vehicle.types'
import { getAppSettings, updateAppSettings } from '@/utils/settings'
import { SortDirection, sorting,type TableSort } from '@/utils/sorting'

interface VehiclesStore {
  // Data
  vehicles: UiVehicle[]
  totalCount: number
  totalPages: number
  currentVehicle: UiVehicle | null

  // UI State
  isLoading: boolean
  error: string | null
  filters: {
    number: string
    model: string
  }
  pagination: {
    sort: TableSort | null;
    page: number;
    limit: number;
    totalPages: number;
    total: number;
  };

  // Actions
  fetchVehicles: () => Promise<void>
  fetchVehicle: (id: string) => Promise<void>
  addVehicle: (v: Omit<UiVehicle, 'id'>) => Promise<UiVehicle | undefined>
  updateVehicle: (id: string, patch: Partial<Omit<UiVehicle, 'id'>>) => Promise<void>
  deleteVehicle: (id: string) => Promise<void>

  // UI Actions
  setFilters: (filters: Partial<VehiclesStore['filters']>) => void
  setPagination: (pagination: Partial<VehiclesStore['pagination']>) => void
  resetFilters: () => void
  clearError: () => void
}

const mapApiToUi = (v: ApiVehicle): UiVehicle => ({
  id: v.ID,
  plate: v.Number,
  brand: v.Model,
  year: v.Make,
  driver: v.Driver ? {
    id: v.Driver.ID,
    fullName: v.Driver.Name,
    phone: v.Driver.Phone,
    active: true,
  } : undefined,
  active: true, // Assuming active is not in API yet or defaulting to true
})

const mapUiToApiCreate = (v: Omit<UiVehicle, 'id'>): CreateVehicleRequest => ({
  Number: v.plate,
  Model: v.brand,
  Make: v.year,
})

const mapUiToApiUpdate = (v: Partial<Omit<UiVehicle, 'id'>>): UpdateVehicleRequest => ({
  Number: v.plate,
  Model: v.brand,
  Make: v.year,
})

export const useVehiclesStore = create<VehiclesStore>((set, get) => ({
  // Initial state
  vehicles: [],
  totalCount: 0,
  totalPages: 0,
  currentVehicle: null,

  isLoading: false,
  error: null,
  filters: {
    number: '',
    model: '',
  },
  pagination: {
    sort: { key: 'number', direction: SortDirection.ASC },
    page: 0,
    limit: getAppSettings().vehiclesLimit ?? 10,
    totalPages: 0,
    total: 0,
  },

  // Data actions
  fetchVehicles: async () => {
    const { filters, pagination } = get()
    set({ isLoading: true, error: null })

    const params: GetVehiclesParams = {
      number: filters.number || undefined,
      model: filters.model || undefined,
      page: pagination.page,
      limit: pagination.limit,
      sort: pagination.sort ? [sorting(pagination.sort)] : undefined,
    }

    try {
      const response = await vehiclesApi.getVehicles(params)
      set({
        vehicles: response.Vehicles.map(mapApiToUi),
        pagination: {
          ...pagination,
          totalPages: Math.ceil(response.Pagination.Total / pagination.limit),
          total: response.Pagination.Total,
        },
      })
    } catch (err: any) {
      const message = err?.message ?? 'Failed to fetch vehicles'
      set({ error: message })
      toast.danger(i18n.t('vehicles.toast.fetch_error'), { description: message })
    } finally {
      set({ isLoading: false })
    }
  },

  fetchVehicle: async (id: string) => {
    set({ isLoading: true, error: null })

    try {
      const response = await vehiclesApi.getVehicle(id)
      set({
        currentVehicle: mapApiToUi(response),
      })
    } catch (err: any) {
      const message = err.message ?? 'Failed to fetch vehicle'
      set({ error: message })
      toast.danger(i18n.t('vehicles.toast.fetch_error'), { description: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  addVehicle: async v => {
    set({ isLoading: true, error: null })

    try {
      const vehicle = await vehiclesApi.createVehicle(mapUiToApiCreate(v))
      const uiVehicle = mapApiToUi(vehicle)
      toast.success(i18n.t('vehicles.toast.create_success'), { description: i18n.t('vehicles.toast.create_success_desc') })

      // Refresh the list to get updated data
      await get().fetchVehicles()

      return uiVehicle
    } catch (err: any) {
      const message = err.message ?? 'Failed to create vehicle'
      set({ error: message })
      toast.danger(i18n.t('vehicles.toast.create_error'), { description: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  updateVehicle: async (id, patch) => {
    set({ isLoading: true, error: null })

    try {
      const updatedVehicle = await vehiclesApi.updateVehicle(id, mapUiToApiUpdate(patch))
      const uiVehicle = mapApiToUi(updatedVehicle)
      toast.success(i18n.t('vehicles.toast.update_success'), { description: i18n.t('vehicles.toast.update_success_desc') })

      // Update current vehicle if it's the one being updated
      set(state => ({
        currentVehicle: state.currentVehicle?.id === id ? uiVehicle : state.currentVehicle,
      }))

      // Refresh the list to get updated data
      await get().fetchVehicles()
    } catch (err: any) {
      const message = err.message ?? 'Failed to update vehicle'
      set({ error: message })
      toast.danger(i18n.t('vehicles.toast.update_error'), { description: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  deleteVehicle: async id => {
    set({ isLoading: true, error: null })

    try {
      await vehiclesApi.deleteVehicle(id)
      toast.success(i18n.t('vehicles.toast.delete_success'), { description: i18n.t('vehicles.toast.delete_success_desc') })

      // Refresh the list to get updated data
      await get().fetchVehicles()
    } catch (err: any) {
      const message = err.message ?? 'Failed to delete vehicle'
      set({ error: message })
      toast.danger(i18n.t('vehicles.toast.delete_error'), { description: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  // UI actions
  setFilters: newFilters => {
    set(state => ({
      filters: { ...state.filters, ...newFilters },
      pagination: { ...state.pagination, page: 1 },
    }))
    // Auto-fetch with new filters
    get().fetchVehicles()
  },

  setPagination: newPagination => {
    set(state => {
      const updatedPagination = { ...state.pagination, ...newPagination }
      if (newPagination.limit !== undefined) {
        updateAppSettings({ vehiclesLimit: newPagination.limit })
      }
      return { pagination: updatedPagination }
    })
    // Auto-fetch with new pagination
    get().fetchVehicles()
  },

  resetFilters: () => {
    const initialLimit = getAppSettings().vehiclesLimit ?? 10
    set({
      filters: {
        number: '',
        model: '',
      },
      pagination: {
        page: 0,
        limit: initialLimit,
        sort: { key: 'Number', direction: SortDirection.ASC },
        totalPages: 1,
        total: 0,
      },
    })
    get().fetchVehicles()
  },

  clearError: () => set({ error: null }),
}))
